/**
 * Stability hardening — global runtime safety nets.
 *
 * Καλείται μία φορά στο App boot. Βάζει:
 *  1. Chunk-load-error auto-recovery (μετά από deploy το browser cache έχει
 *     stale chunk hashes → "Failed to load module" → hard reload μία φορά).
 *  2. Global axios timeout + cleaner error logging.
 *  3. Unhandled promise rejection logger (για να φαίνεται καθαρά τι έσπασε).
 */
import axios from "axios";
import { toast } from "sonner";

const RELOAD_FLAG = "pn-chunk-reload-attempted";

export function installStabilityGuards() {
  // ─── 1. Chunk loading errors (Webpack/React lazy splits) ────────────
  const isChunkError = (e) => {
    if (!e) return false;
    const msg = String(e?.message || e?.reason?.message || e || "");
    return (
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk") ||
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Importing a module script failed") ||
      msg.includes("ChunkLoadError")
    );
  };

  const handleChunkError = (e) => {
    if (!isChunkError(e)) return;
    // Don't loop — try only once per session
    if (sessionStorage.getItem(RELOAD_FLAG)) return;
    sessionStorage.setItem(RELOAD_FLAG, "1");
    // eslint-disable-next-line no-console
    console.warn("[stability] Chunk load failure detected, hard-reloading…", e);
    setTimeout(() => window.location.reload(), 300);
  };

  window.addEventListener("error", handleChunkError);
  window.addEventListener("unhandledrejection", (ev) => handleChunkError(ev));

  // Clear the flag on successful page load (so it resets every session)
  window.addEventListener("load", () => {
    setTimeout(() => sessionStorage.removeItem(RELOAD_FLAG), 5000);
  });

  // ─── 2. Global axios timeout default ─────────────────────────────────
  // Without a default, hanging requests freeze components indefinitely.
  axios.defaults.timeout = axios.defaults.timeout || 30000;

  // ─── 3. Generic unhandled-rejection notifier (silent — only console) ─
  window.addEventListener("unhandledrejection", (ev) => {
    if (isChunkError(ev)) return;
    // eslint-disable-next-line no-console
    console.error("[stability] Unhandled promise rejection:", ev.reason);
  });

  // ─── 4. Network status indicator (offline toast) ────────────────────
  window.addEventListener("offline", () => {
    try { toast.error("Δεν υπάρχει σύνδεση στο internet"); } catch { /* ignore */ }
  });
  window.addEventListener("online", () => {
    try { toast.success("Επανήλθε η σύνδεση"); } catch { /* ignore */ }
  });
}
